// What a list is narrowed to, kept in the address (module 10 U228; pipeline F1, B1950): ?q= for the find, and one
// parameter a chip or a drop-down (?show=, ?company=, ?market= ...). A reload or Back draws the same list, and an
// address sent to a colleague opens it narrowed the same way, read through their own rails.
//
// replaceState, never pushState: narrowing a list is not a page of its own, so Back leaves the page rather than
// stepping back through every key typed. The router (App.tsx, B48) does not hear a replaceState, and has no need to.
import { useCallback, useEffect, useRef, useState } from 'react';

export type Params<K extends string> = Record<K, string>;

/** The named parameters of an address's query, '' for any that is not there. */
export function readParams<K extends string>(search: string, keys: readonly K[]): Params<K> {
  const u = new URLSearchParams(search);
  const out = {} as Params<K>;
  for (const k of keys) out[k] = u.get(k) ?? '';
  return out;
}

/** The query with the changes made: a value sets its parameter, '' removes it, and every other parameter is kept. */
export function withParams(search: string, changes: Record<string, string | undefined>): string {
  const u = new URLSearchParams(search);
  for (const [k, v] of Object.entries(changes)) {
    if (v === undefined) continue;
    if (v) u.set(k, v); else u.delete(k);
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

/**
 * The page's filters, read from the address and written back to it. The address is followed when something else
 * changes it: Back to this page, or the menu opening the same page bare, which re-renders the page without
 * remounting it (the router keeps the component and pushes the new address).
 */
export function useFilterParams<K extends string>(keys: readonly K[]): [Params<K>, (changes: Partial<Params<K>>) => void] {
  const [params, setParams] = useState<Params<K>>(() => readParams(window.location.search, keys));
  const keysRef = useRef(keys);
  // The query this hook last read or wrote. Anything else in the address was put there by a navigation.
  const seen = useRef(window.location.search);

  useEffect(() => {
    const follow = () => {
      if (window.location.search === seen.current) return;
      seen.current = window.location.search;
      setParams(readParams(window.location.search, keysRef.current));
    };
    follow();
    window.addEventListener('popstate', follow);
    return () => window.removeEventListener('popstate', follow);
  });

  const set = useCallback((changes: Partial<Params<K>>) => {
    setParams((prev) => ({ ...prev, ...changes }));
    const search = withParams(window.location.search, changes as Record<string, string | undefined>);
    seen.current = search;
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}${window.location.hash}`);
  }, []);

  return [params, set];
}

/** One of a page's chip values, or its first when the address holds anything else. */
export function oneOf<T extends string>(raw: string, values: readonly T[], fallback: T): T {
  return (values as readonly string[]).includes(raw) ? (raw as T) : fallback;
}
